import { useEffect, useState, type ReactNode } from "react";
import { useEstadoSync, type EstadoSync } from "../lib/useEstadoSync";
import { dbLocal } from "../lib/db-local";
import { VentasRechazadasModal } from "./VentasRechazadas";
import { useApi } from "../lib/useApi";
import {
  navegar,
  seccionDeRuta,
  pildorasDe,
  etiquetaGrupoPrincipal,
  SECCIONES,
  VISTAS,
  RUTAS_POS,
  RUTAS_MOSTRADOR,
  RUTAS_INVENTARIO,
  RUTAS_PANEL_CELULAR,
  PUNTO_DE_VENTA,
  type RutaId,
  type Seccion,
  type SeccionIcono,
} from "../lib/ruta";
import { useMasAngostoQue, UMBRAL_FILA_PX, UMBRAL_PANEL_PX } from "../lib/pantalla";
import { EVENTO_MAPA } from "../lib/avisos";
import type { SesionActiva, Rol } from "../lib/tipos";
import { MiContrasenaModal } from "./MiContrasenaModal";
import {
  Sidebar,
  SidebarGroup,
  NavItem,
  SidebarSyncLine,
  UserCard,
  Topbar,
  Avatar,
  SyncPill,
  Tabs,
  TabPill,
  ToastProvider,
  ToastStack,
  useToast,
  Logo,
  IconHoy,
  IconCasos,
  IconVentas,
  IconClientes,
  IconInventario,
  IconCompras,
  IconCatalogo,
  IconSugerencias,
  IconAjustes,
  IconMostrador,
  IconRecepcion,
  IconAvisos,
  IconMapa,
  IconAprendizaje,
  type SyncTono,
} from "./ui";

type Props = {
  sesion: SesionActiva;
  rutaActual: RutaId;
  onSalir: () => void;
  children: ReactNode;
};

const ROL_LABEL: Record<Rol, string> = {
  super_admin: "Dueño",
  admin_sucursal: "Encargado",
  operador: "Vendedor",
  lector_reportes: "Lector",
};

const ICONOS: Record<SeccionIcono, (p: { size?: number }) => ReactNode> = {
  hoy: IconHoy,
  casos: IconCasos,
  ventas: IconVentas,
  clientes: IconClientes,
  inventario: IconInventario,
  compras: IconCompras,
  catalogo: IconCatalogo,
  sugerencias: IconSugerencias,
  ajustes: IconAjustes,
  mapa: IconMapa,
  aprendizaje: IconAprendizaje,
};

// Un ícono por pantalla también en «Punto de venta»: Avisos repetía el de Clientes.
const ICONOS_PUNTO_DE_VENTA: Partial<Record<RutaId, (p: { size?: number }) => ReactNode>> = {
  mostrador: IconMostrador,
  recepcion: IconRecepcion,
  reposiciones: IconAvisos,
};

// Título de la página en el topbar (por ruta; si no, cae al label de la sección). Es el mismo nombre
// del menú y del Mapa (R-F): una pantalla, un nombre.
const TITULOS: Partial<Record<RutaId, string>> = {
  hoy: "Hoy",
  casos: "Casos",
  dashboard: "Ventas y caja",
  caja: "Ventas y caja",
  consolidado: "Ventas y caja",
  inventario: "Inventario",
  "por-vencer": "Inventario",
  "stock-bajo": "Inventario",
  "recepciones-pendientes": "Inventario",
  faltantes: "Inventario",
  conteo: "Inventario",
  pedido: "Compras",
  proveedores: "Compras",
  ajustes: "Ajustes",
  mapa: "Mapa del sistema",
  catalogo: "Catálogo",
  "importar-catalogo": "Catálogo",
  "catalogo-prueba": "Catálogo de prueba",
  sugerencias: "Sugerencias del mostrador",
  copiloto: "Copiloto",
  usuarios: "Usuarios y roles",
  sucursales: "Boticas",
  grabadores: "Grabadores",
  "audio-calidad": "El oído",
  mostrador: "Mostrador",
  recepcion: "Recepción",
  reposiciones: "Avisos",
  clientes: "Clientes y seguimiento",
  reconocimiento: "Clientes y seguimiento",
  ingreso: "Inventario",
  "mi-cuenta": "Mi cuenta",
  examen: "Examen del día",
  aprendizaje: "Aprendizaje",
};

function iniciales(nombre: string): string {
  // Solo tokens que empiezan con letra (evita que "Kevin (super)" rinda "K(").
  const tokens = nombre.trim().split(/\s+/).filter((t) => /\p{L}/u.test(t.charAt(0)));
  const a = tokens[0]?.[0] ?? "";
  const b = tokens.length > 1 ? (tokens[tokens.length - 1]?.[0] ?? "") : "";
  return (a + b).toUpperCase() || "?";
}

// EstadoSync (device-local, real) → SyncPill del topbar. El estado EN VIVO por-botica es
// "próximamente" (no hay heartbeat), así que la píldora refleja SOLO este equipo, con honestidad.
function pill(estado: EstadoSync): { tono: SyncTono; texto: string } {
  if (estado.nivel === "rechazada") return { tono: "offline", texto: `${estado.rechazadas} venta(s) rechazada(s) — revisar` };
  if (estado.nivel === "offline") return { tono: "offline", texto: estado.pendientes > 0 ? `Sin conexión · ${estado.pendientes} guardadas en este equipo` : "Sin conexión — las ventas se guardan igual" };
  if (estado.nivel === "pendiente") return { tono: "pendiente", texto: `${estado.pendientes} por sincronizar` };
  return { tono: "online", texto: "Este equipo está en línea" };
}

function fechaLarga(): string {
  const s = new Date().toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const sinComa = s.replace(",", "");
  return sinComa.charAt(0).toUpperCase() + sinComa.slice(1);
}

type HoyBadges = { atencion?: { casos_abiertos?: number; recepciones_pendientes?: number }; boticas?: unknown[] };

const puede = (rol: Rol, id: RutaId) => VISTAS.find((v) => v.id === id)?.roles.includes(rol) ?? false;

// El contador ⭐ del ítem «Mapa»: lo que le toca a la puesta en marcha (Mapa del tesoro, tramo 05 de
// huayruro-orden-y-mapa). SOLO el Dueño lo pide —a los demás el servidor les respondería 403— y sale de
// /mapa/estado, no del resumen de Hoy que leen los cuatro roles (OM-12). Se relee cuando el Mapa avisa
// que cambió una marca.
function useEstrellasMapa(rol: Rol): number {
  const { data, recargar } = useApi<{ resumen: { te_tocan: number } }>(rol === "super_admin" ? "/mapa/estado" : null);
  useEffect(() => {
    window.addEventListener(EVENTO_MAPA, recargar);
    return () => window.removeEventListener(EVENTO_MAPA, recargar);
  }, [recargar]);
  return data?.resumen.te_tocan ?? 0;
}

export function Layout({ sesion, rutaActual, onSalir, children }: Props) {
  const estado = useEstadoSync(dbLocal);
  const rol = sesion.usuario.rol;
  // R-A: en pantalla angosta manda el equipo, no la ruta. Las 5 pantallas del mostrador (y los alias
  // de Inventario) van en el shell del celular para quien vende o manda en la botica; el lector nunca
  // recibe ese shell. Desde el umbral hacia arriba, todo sigue como antes.
  const angosto = useMasAngostoQue(UMBRAL_PANEL_PX);
  // Y en el celular (bajo 768 px), las pantallas del panel que ya se leen a 390 (P2: Hoy, Casos y Mapa)
  // también van en ese marco, con «☰ Menú» para llegar al resto.
  const celular = useMasAngostoQue(UMBRAL_FILA_PX);
  const esPos =
    rol === "operador" ||
    RUTAS_POS.includes(rutaActual) ||
    (angosto && rol !== "lector_reportes" && RUTAS_MOSTRADOR.includes(rutaActual)) ||
    (celular && rol !== "lector_reportes" && RUTAS_PANEL_CELULAR.includes(rutaActual));
  return esPos ? (
    <PosShell sesion={sesion} rutaActual={rutaActual} onSalir={onSalir} estado={estado}>
      {children}
    </PosShell>
  ) : (
    <PanelShell sesion={sesion} rutaActual={rutaActual} onSalir={onSalir} estado={estado}>
      {children}
    </PanelShell>
  );
}

// ---- Panel de escritorio (dueño / encargado / lector): Sidebar 238 + Topbar 60 ----
function PanelShell({ sesion, rutaActual, onSalir, estado, children }: Props & { estado: EstadoSync }) {
  const rol = sesion.usuario.rol;
  const [clave, setClave] = useState(false);
  const [verRechazadas, setVerRechazadas] = useState(false);
  const secciones = SECCIONES.filter((s) => s.roles.includes(rol));
  const principal = secciones.filter((s) => s.grupo === "cadena");
  const admin = secciones.filter((s) => s.grupo === "admin");
  const puntoDeVenta = PUNTO_DE_VENTA.filter((id) => puede(rol, id));
  const activa = seccionDeRuta(rutaActual);
  const p = pill(estado);

  // Badges del sidebar desde el resumen del dueño (cadena para super, su botica para encargado).
  const { data } = useApi<HoyBadges>("/hoy/resumen");
  const nCasos = data?.atencion?.casos_abiertos ?? 0;
  const nRecep = data?.atencion?.recepciones_pendientes ?? 0;
  const nMapa = useEstrellasMapa(rol);
  const badgeDe = (b: Seccion["badge"]): { texto: string; variant?: "danger" | "dark" } | undefined => {
    if (b === "casos" && nCasos > 0) return { texto: String(nCasos), variant: "danger" };
    if (b === "recepciones" && nRecep > 0) return { texto: String(nRecep), variant: "dark" };
    // Oscuro y no rojo: es la lista de la puesta en marcha, no una alarma.
    if (b === "mapa" && nMapa > 0) return { texto: String(nMapa), variant: "dark" };
    return undefined;
  };

  const ini = iniciales(sesion.usuario.nombre);
  // Alcance real: el super ve el nº de boticas de SU tenant (de /hoy/resumen), no una cifra fija.
  const nBoticas = data?.boticas?.length ?? 0;
  const alcance =
    rol === "super_admin"
      ? nBoticas > 0
        ? `${nBoticas} ${nBoticas === 1 ? "botica" : "boticas"}`
        : "Cadena"
      : (sesion.sucursal?.nombre ?? "Cadena");
  const sideDot = estado.online && estado.nivel !== "rechazada" ? "online" : "offline";
  const sideText = estado.nivel === "ok" ? "Este equipo al día" : estado.nivel === "pendiente" ? `${estado.pendientes} por sincronizar` : estado.nivel === "rechazada" ? `${estado.rechazadas} rechazada(s)` : "Sin conexión";

  const items = (s: Seccion) => {
    const Ico = ICONOS[s.icono];
    const b = badgeDe(s.badge);
    return (
      <NavItem
        key={s.id}
        icon={<Ico />}
        label={s.label}
        active={activa?.id === s.id}
        onClick={() => navegar(s.ruta)}
        {...(b ? { badge: b } : {})}
      />
    );
  };

  return (
    <ToastProvider>
      <div className="flex h-screen w-full min-w-[1200px] overflow-hidden bg-app text-ink">
        <Sidebar
          footer={
            <>
              <SidebarSyncLine dot={sideDot} text={sideText} animated={!estado.online} />
              <UserCard initials={ini} name={sesion.usuario.nombre} role={`${ROL_LABEL[rol]} · ${alcance}`} />
              <div className="mx-6 mb-4 -mt-1 flex items-center gap-3 self-start">
                <button type="button" onClick={() => setClave(true)} className="text-[11.5px] font-medium text-ink-3 transition-colors hover:text-ink-emph">
                  Mi contraseña
                </button>
                <button type="button" onClick={onSalir} className="text-[11.5px] font-medium text-ink-3 transition-colors hover:text-accent-ink">
                  Cerrar sesión
                </button>
              </div>
            </>
          }
        >
          <SidebarGroup label={etiquetaGrupoPrincipal(rol)}>{principal.map(items)}</SidebarGroup>
          {/* Acceso al POS desde el panel (simétrico al "← Panel" del shell POS). Se arma con los roles
              de cada ruta: el lector no vende y el dueño ya no tiene Mostrador ni Recepción (RC-6). */}
          {puntoDeVenta.length > 0 && (
            <SidebarGroup label="Punto de venta">
              {puntoDeVenta.map((id) => {
                const Ico = ICONOS_PUNTO_DE_VENTA[id] ?? IconMostrador;
                const v = VISTAS.find((x) => x.id === id);
                return <NavItem key={id} icon={<Ico />} label={v?.label ?? id} onClick={() => navegar(id)} />;
              })}
            </SidebarGroup>
          )}
          {admin.length > 0 && <SidebarGroup label="Administrar">{admin.map(items)}</SidebarGroup>}
        </Sidebar>

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar
            title={TITULOS[rutaActual] ?? activa?.label ?? ""}
            date={fechaLarga()}
            right={
              estado.nivel === "rechazada" ? (
                <button type="button" onClick={() => setVerRechazadas(true)} aria-haspopup="dialog" className="rounded-full" data-testid="ver-rechazadas">
                  <SyncPill tono={p.tono} texto={p.texto} className="underline decoration-dotted underline-offset-2" />
                </button>
              ) : (
                <SyncPill tono={p.tono} texto={p.texto} />
              )
            }
            initials={ini}
          />
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-[1150px] px-7 pt-[26px] pb-[70px]">
              <Pildoras rol={rol} rutaActual={rutaActual} />
              {children}
            </div>
          </main>
        </div>
      </div>
      {verRechazadas && <VentasRechazadasModal db={dbLocal} onCerrar={() => setVerRechazadas(false)} />}
      {clave && <ClaveConAviso onCerrar={() => setClave(false)} />}
      <ToastStack />
    </ToastProvider>
  );
}

// «Mi contraseña» con su aviso de listo (tiene que vivir dentro del ToastProvider del shell).
function ClaveConAviso({ onCerrar }: { onCerrar: () => void }) {
  const toast = useToast();
  return (
    <MiContrasenaModal
      onCerrar={onCerrar}
      onListo={() => {
        toast("Tu contraseña quedó cambiada.");
        onCerrar();
      }}
    />
  );
}

// R-E: dos niveles y no más. Ventas y caja, Compras y Catálogo muestran sus pantallas hermanas como
// píldoras; cada una solo aparece si el rol puede abrirla (el lector ve Ventas · Caja).
function Pildoras({ rol, rutaActual }: { rol: Rol; rutaActual: RutaId }) {
  const grupo = pildorasDe(rutaActual);
  if (!grupo) return null;
  const visibles = grupo.rutas.filter((r) => puede(rol, r));
  if (visibles.length < 2) return null;
  return (
    <Tabs className="mb-[18px] flex-wrap">
      {visibles.map((r) => (
        <TabPill key={r} active={r === rutaActual} onClick={() => navegar(r)} className="max-md:min-h-[44px]">
          {grupo.etiquetas[r] ?? r}
        </TabPill>
      ))}
    </Tabs>
  );
}

// ---- Shell POS (mostrador): mobile/touch-first, sin sidebar de escritorio ----
function PosShell({ sesion, rutaActual, onSalir, estado, children }: Props & { estado: EstadoSync }) {
  const rol = sesion.usuario.rol;
  const vistas = VISTAS.filter((v) => v.grupo === "pos" && v.roles.includes(rol));
  const p = pill(estado);
  const tienePanel = rol === "admin_sucursal" || rol === "super_admin";
  const esVendedora = rol === "operador";
  // R-B: bajo 768 px los destinos van en UNA fila de posiciones iguales y «← Panel» sube a la
  // cabecera como «☰ Menú». Antes eran 2 o 3 filas de chips y se comían hasta el 43 % de la pantalla.
  const fila = useMasAngostoQue(UMBRAL_FILA_PX);
  const [menu, setMenu] = useState(false);
  const [clave, setClave] = useState(false);
  const [verRechazadas, setVerRechazadas] = useState(false);
  const ini = iniciales(sesion.usuario.nombre);
  // OA-2: la cabecera dice quién tiene la sesión abierta en ESTE equipo y en qué botica.
  const quien = `${ROL_LABEL[rol]} · ${sesion.sucursal?.nombre ?? "Toda la cadena"}`;
  // El texto «N en cola» también lo lee el e2e para saber que la cola se vació (acciones.ts).
  const textoPill = estado.nivel === "rechazada" ? p.texto : p.tono === "online" ? "En línea" : p.tono === "pendiente" ? `${estado.pendientes} en cola` : "Sin conexión";
  const activo = (id: RutaId) => rutaActual === id || (id === "inventario" && RUTAS_INVENTARIO.includes(rutaActual));

  return (
    <ToastProvider>
      {/* En celular la página se desplaza entera; desde 768 px el shell ocupa la ventana y cada
          pantalla se desplaza por dentro (así el Mostrador de escritorio no empuja «Cobrar» fuera). */}
      <div className="flex min-h-screen flex-col bg-app text-ink md:h-dvh md:min-h-0">
        <header className="flex flex-none items-center gap-2.5 border-b border-line-chrome bg-topbar px-3 py-2 sm:px-4">
          {/* Bajo 375 px el logo cede su lugar al nombre de quien tiene la sesión (OA-2, tramo 07). */}
          <Logo size={22} className="flex-none max-[374px]:hidden" />
          <span className="flex min-w-0 items-center gap-2" data-testid="sesion-actual">
            <Avatar initials={ini} size={30} />
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-[13px] font-bold text-ink">{sesion.usuario.nombre}</span>
              <span className="truncate text-[11.5px] text-ink-2">{quien}</span>
            </span>
          </span>
          <span className="flex-1" />
          {/* D11: con ventas rechazadas, el aviso se toca y abre la lista de ESTE equipo. */}
          {estado.nivel === "rechazada" ? (
            <button type="button" onClick={() => setVerRechazadas(true)} aria-haspopup="dialog" className="min-h-[44px] flex-none rounded-full" data-testid="ver-rechazadas">
              <SyncPill tono={p.tono} texto={textoPill} className="whitespace-nowrap underline decoration-dotted underline-offset-2" />
            </button>
          ) : (
            <SyncPill tono={p.tono} texto={textoPill} className="flex-none whitespace-nowrap" />
          )}
          {/* Tramo 07: «☰ Menú» para todos en el celular — la vendedora lo abre para «Mi contraseña» y
              «Cerrar sesión». Un tercer botón en su cabecera a 390 le recortaba el nombre, que es lo que
              dice quién tiene la sesión abierta (OA-2). Desde 768 px, «Mi contraseña» va junto a «Salir».
              Brief 26-oct-2026 (Kevin: «no puede acceder a su cuenta y ver su examen»): para la vendedora,
              «Mi cuenta» ocupa ese mismo lugar en las dos medidas —su página trae la contraseña, la salida,
              «Mi aprendizaje» y el examen— y anuncia el examen pendiente. */}
          {esVendedora ? (
            <>
              <BotonMiCuenta compacto={fila} activo={rutaActual === "mi-cuenta" || rutaActual === "examen"} />
              {/* En la compu del mostrador, que se comparte, salir sigue a un toque. */}
              {!fila && (
                <button type="button" onClick={onSalir} className="min-h-[44px] flex-none px-2 text-[13px] font-medium text-ink-2 hover:text-accent-ink">
                  Salir
                </button>
              )}
            </>
          ) : fila ? (
            <button
              type="button"
              onClick={() => setMenu(true)}
              aria-haspopup="dialog"
              aria-expanded={menu}
              className="min-h-[44px] flex-none rounded-[9px] px-2.5 text-[13px] font-semibold text-ink-emph hover:bg-hover-btn"
            >
              ☰ Menú
            </button>
          ) : (
            <>
              <button type="button" onClick={() => setClave(true)} className="min-h-[44px] flex-none px-2 text-[13px] font-medium text-ink-2 hover:text-ink-emph">
                Mi contraseña
              </button>
              <button type="button" onClick={onSalir} className="min-h-[44px] flex-none px-2 text-[13px] font-medium text-ink-2 hover:text-accent-ink">
                Salir
              </button>
            </>
          )}
        </header>

        {fila ? (
          <nav
            aria-label="Destinos del mostrador"
            className="grid flex-none gap-1.5 border-b border-line-chrome bg-topbar px-3 py-2"
            style={{ gridTemplateColumns: `repeat(${Math.max(vistas.length, 1)}, minmax(0, 1fr))` }}
          >
            {vistas.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => navegar(v.id)}
                aria-current={activo(v.id) ? "page" : undefined}
                className={
                  // Bajo 375 px (celulares de 360) cada posición mide 62.4 px y «Mostrador» y «Recepción»
                  // se cortaban a 11.5 px: medido en las capturas del tramo 03.
                  "flex min-h-[56px] min-w-0 flex-col items-center justify-center gap-1 rounded-[10px] border px-0.5 text-[11.5px] font-semibold leading-none transition-colors max-[374px]:text-[10.5px] max-[374px]:tracking-[-0.02em] " +
                  (activo(v.id) ? "border-ink-surface-2 bg-ink-surface-2 text-on-dark" : "border-line-input bg-card text-nav-inactive hover:bg-hover-nav")
                }
              >
                <span aria-hidden="true" className="text-[18px] leading-none">
                  {v.icono}
                </span>
                <span className="max-w-full truncate">{v.label}</span>
              </button>
            ))}
          </nav>
        ) : (
          <nav aria-label="Destinos del mostrador" className="flex flex-none flex-wrap gap-1.5 border-b border-line-chrome bg-topbar px-3 py-2">
            {vistas.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => navegar(v.id)}
                aria-current={activo(v.id) ? "page" : undefined}
                className={
                  "inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[13px] font-semibold transition-colors " +
                  (activo(v.id) ? "border-ink-surface-2 bg-ink-surface-2 text-on-dark" : "border-line-input bg-card text-nav-inactive hover:bg-hover-nav")
                }
              >
                <span aria-hidden="true">{v.icono}</span>
                {v.label}
              </button>
            ))}
            {tienePanel && (
              <button
                type="button"
                onClick={() => navegar("hoy")}
                className="ml-auto inline-flex min-h-[44px] flex-none items-center gap-1.5 whitespace-nowrap rounded-full border border-line-input bg-card px-3.5 text-[13px] font-semibold text-nav-inactive hover:bg-hover-nav"
              >
                ← Panel
              </button>
            )}
          </nav>
        )}

        <main className="flex-1 p-3 md:min-h-0 md:overflow-y-auto">
          {/* Tramo 07: Catálogo entró al marco del celular y sus píldoras («Productos · Importar») son el
              único camino a Importar; sin ellas, Importar quedaba sin botón en el celular (error 1). */}
          {RUTAS_PANEL_CELULAR.includes(rutaActual) && <Pildoras rol={rol} rutaActual={rutaActual} />}
          {children}
        </main>
      </div>
      {menu && (
        <MenuHoja
          rol={rol}
          rutaActual={rutaActual}
          onCerrar={() => setMenu(false)}
          onSalir={onSalir}
          onClave={() => {
            setMenu(false);
            setClave(true);
          }}
        />
      )}
      {clave && <ClaveConAviso onCerrar={() => setClave(false)} />}
      {verRechazadas && <VentasRechazadasModal db={dbLocal} onCerrar={() => setVerRechazadas(false)} />}
      <ToastStack />
    </ToastProvider>
  );
}

// «Mi cuenta» de la vendedora (brief 26-oct-2026, paso 2): lleva a SU página, no al panel. Avisa el examen
// pendiente del día y las tareas sin hacer; se relee cuando «Mi cuenta» o el examen cambian algo y al
// volver a la pestaña (con el lector de por medio, ella pasa horas en el Ingreso).
export const EVENTO_MI_CUENTA = "huayruro:mi-cuenta";
type ResumenMiCuenta = { examen: "sin_carga" | "pendiente" | "en_curso" | "terminado"; tareas_pendientes: number; aprobado?: boolean };

function BotonMiCuenta({ compacto, activo }: { compacto: boolean; activo: boolean }) {
  const { data, recargar } = useApi<ResumenMiCuenta>("/mi-cuenta/resumen", [], { cadaMs: 120_000 });
  useEffect(() => {
    window.addEventListener(EVENTO_MI_CUENTA, recargar);
    return () => window.removeEventListener(EVENTO_MI_CUENTA, recargar);
  }, [recargar]);
  const examen = data?.examen === "pendiente" ? "examen pendiente" : data?.examen === "en_curso" ? "examen sin terminar" : null;
  const tareas = data && data.tareas_pendientes > 0 ? `${data.tareas_pendientes} ${data.tareas_pendientes === 1 ? "tarea" : "tareas"}` : null;
  const aviso = examen ?? tareas;
  return (
    <button
      type="button"
      onClick={() => navegar("mi-cuenta")}
      aria-current={activo ? "page" : undefined}
      aria-label={aviso ? `Mi cuenta · ${aviso}` : "Mi cuenta"}
      data-testid="boton-mi-cuenta"
      className={
        // En el celular sin ícono y más angosto: el nombre de la cabecera (OA-2) no se puede recortar.
        (compacto ? "relative flex min-h-[44px] flex-none items-center rounded-[10px] border px-2.5 text-left transition-colors " : "relative flex min-h-[44px] flex-none items-center gap-2 rounded-[10px] border px-3 text-left transition-colors ") +
        (activo ? "border-ink-surface-2 bg-ink-surface-2 text-on-dark" : aviso ? "border-accent bg-accent-soft text-accent-ink hover:bg-hover-btn" : "border-line-input bg-card text-ink-emph hover:bg-hover-btn")
      }
    >
      {!compacto && <IconAprendizaje size={16} />}
      <span className="flex flex-col leading-tight">
        <span className="text-[13px] font-semibold">Mi cuenta</span>
        {/* En el celular el aviso va en una segunda línea chica: el nombre de la cabecera no se recorta. */}
        {aviso && <span className={compacto ? "text-[10.5px] font-semibold" : "text-[11.5px] font-semibold"}>{aviso}</span>}
      </span>
      {examen && !activo && <span aria-hidden="true" className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-accent ring-2 ring-topbar" />}
    </button>
  );
}

// «☰ Menú» del celular (dueño y encargado): el menú del panel en una hoja lateral, con los mismos
// grupos y nombres que el sidebar, y «Cerrar sesión» al pie. Los destinos del mostrador no se repiten:
// ya están a la vista en la fila.
function MenuHoja({ rol, rutaActual, onCerrar, onSalir, onClave }: { rol: Rol; rutaActual: RutaId; onCerrar: () => void; onSalir: () => void; onClave: () => void }) {
  const secciones = SECCIONES.filter((s) => s.roles.includes(rol));
  const principal = secciones.filter((s) => s.grupo === "cadena");
  const admin = secciones.filter((s) => s.grupo === "admin");
  const activa = seccionDeRuta(rutaActual);
  const nMapa = useEstrellasMapa(rol);

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [onCerrar]);

  const ir = (s: Seccion) => {
    onCerrar();
    navegar(s.ruta);
  };
  const item = (s: Seccion) => {
    const Ico = ICONOS[s.icono];
    const badge = s.badge === "mapa" && nMapa > 0 ? { badge: { texto: String(nMapa), variant: "dark" as const } } : {};
    return <NavItem key={s.id} icon={<Ico />} label={s.label} active={activa?.id === s.id} onClick={() => ir(s)} className="min-h-[44px]" {...badge} />;
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink-surface/40" onClick={onCerrar}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menú"
        className="flex h-full w-[min(320px,88vw)] flex-col overflow-y-auto border-l border-line-chrome bg-sidebar shadow-[0_10px_30px_rgba(36,29,26,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-none items-center justify-between gap-2 border-b border-line-chrome px-4 py-2">
          <span className="text-[15px] font-bold text-ink">Menú</span>
          <button type="button" onClick={onCerrar} className="min-h-[44px] rounded-[9px] px-3 text-[13px] font-semibold text-ink-emph hover:bg-hover-btn">
            Cerrar
          </button>
        </div>
        {principal.length + admin.length > 0 ? (
          <nav className="flex-1 py-3">
            <SidebarGroup label={etiquetaGrupoPrincipal(rol)}>{principal.map(item)}</SidebarGroup>
            {admin.length > 0 && <SidebarGroup label="Administrar">{admin.map(item)}</SidebarGroup>}
          </nav>
        ) : (
          // La vendedora no tiene secciones del panel: su hoja trae solo su cuenta, arriba y no al fondo
          // de una hoja vacía (se veía rota en la captura del tramo 07).
          <p className="px-4 pb-2 pt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-2">Tu cuenta</p>
        )}
        <button
          type="button"
          onClick={onClave}
          className="mx-4 mb-2 min-h-[44px] flex-none rounded-[9px] border border-line-input bg-card text-[13px] font-semibold text-ink-emph hover:bg-hover-btn"
        >
          Mi contraseña
        </button>
        <button
          type="button"
          onClick={onSalir}
          className="mx-4 mb-4 min-h-[44px] flex-none rounded-[9px] border border-line-input bg-card text-[13px] font-semibold text-ink-emph hover:bg-hover-btn"
        >
          Cerrar sesión
        </button>
        {principal.length + admin.length === 0 && <div className="flex-1" />}
      </div>
    </div>
  );
}
